import { belongsToCurrentPage } from 'helpers/candidate_application/form-page-helpers';
import { processDynamicContent } from 'helpers/candidate_application/process-dynamic-content';
import {
  getAcicPrimaryUseConfigKey,
  getCitizenshipsToCheck,
  getCountriesToCheck,
  getCurrentAddressOccupancy,
  getFathersNameResolved,
  getFullName,
  getHasFaceSimilarityPassed,
  getHasIdVerifiedByProvider,
  getIdRequirementsListsUsed,
  getIdTypesSelected,
  getIdValuesSelected,
  getIsDobIdSelected,
  getIsFcra,
  getIsPhotoIdSelected,
  getNationalityAlpha2,
  getNationalityFullName,
  getOtherNameLinkageSectionRequired,
  getPassportOrIdNumber,
  getPreviousAddressesOccupancy,
  getRoleLocationName,
  getStoredValue,
  getUniqueCountries,
  hasIndiaInAddressOrQualifications,
  hasUsEmployerInEmploymentHistory,
} from 'helpers/candidate_application/processors';
import { convertObjectToDotPaths, splitArrayPath } from 'helpers/field-paths';
import functionValidators from 'helpers/validators/function-validators';

// Map of all processors for hidden values
const SOURCES = {
  getStoredValue,
  // Country processors
  getUniqueCountries,
  getCountriesToCheck,
  // Address occupancy processors
  getCurrentAddressOccupancy,
  getPreviousAddressesOccupancy,
  // ID verification processors
  getNationalityAlpha2,
  getNationalityFullName,
  getPassportOrIdNumber,
  getIsPhotoIdSelected,
  getIsDobIdSelected,
  getIsFcra,
  getHasFaceSimilarityPassed,
  getHasIdVerifiedByProvider,
  getIdValuesSelected,
  getIdTypesSelected,
  getIdRequirementsListsUsed,
  getAcicPrimaryUseConfigKey,
  // Template helper processors (avoid CSP issues)
  getFullName,
  getRoleLocationName,
  // Other processors
  getCitizenshipsToCheck,
  getFathersNameResolved,
  getOtherNameLinkageSectionRequired,
  hasIndiaInAddressOrQualifications,
  hasUsEmployerInEmploymentHistory,
};

// Helper functions
const hasNonEmpty = v => v !== undefined && v !== null && v !== '';

/**
 * Match an actual field path against a watched pattern (supports {i}).
 * Returns { isMatch, parentCode, arrayIndex, childCode } or null.
 */
export const matchDependencyPattern = (watchedPattern, actualFieldPath) => {
  if (!watchedPattern || !actualFieldPath) return null;

  // watched can contain "{i}"
  const {
    parentCode: wParent,
    hasIndexPlaceholder: wHasI,
    childCode: wChild,
    pathNoIndex: wPathNoIndex,
  } = splitArrayPath(watchedPattern);

  // actual has real numeric index (or not)
  const {
    parentCode: aParent,
    arrayIndex: aIndex,
    childCode: aChild,
    pathNoIndex: aPathNoIndex,
  } = splitArrayPath(actualFieldPath);

  if (!wParent || !aParent) return null;

  // Case 1: parent-only watch: "a" => match any a.*
  if (wParent && !wChild && !wHasI) {
    if (aParent === wParent) {
      return {
        isMatch: true,
        actualPath: actualFieldPath,
        parentCode: aParent,
        arrayIndex: aIndex,
        childCode: aChild,
      };
    }
    return null;
  }

  // Case 2: watched has fixed child: "a.b" or "a.0.b" => match parent and child
  if (wParent && wChild) {
    const matches = aPathNoIndex === wPathNoIndex;
    if (matches)
      return {
        isMatch: true,
        actualPath: actualFieldPath,
        parentCode: aParent,
        arrayIndex: aIndex,
        childCode: aChild,
      };
    return null;
  }

  return null;
};

/**
 * Gets the dependents from a dependency map without injecting indexes.
 * Use for processing dynamic dependencies and hidden fields.
 */
export const getDependents = (name, dependencyMap) => {
  if (!name || !dependencyMap) return [];

  const out = [];
  Object.entries(dependencyMap || {}).forEach(([watchedPattern, dependents = []]) => {
    const matchResult = matchDependencyPattern(watchedPattern, name);
    if (matchResult?.isMatch) {
      out.push(...dependents);
    }
  });

  return out;
};

/**
 * Gets the dependents from a dependency map with actual field names.
 * Use for validation dependencies where you need the actual field paths.
 */
export const getDependentsWithIndexes = (name, dependencyMap) => {
  if (!name || !dependencyMap) return [];

  const out = [];
  Object.entries(dependencyMap || {}).forEach(([watchedPattern, dependents = []]) => {
    const matchResult = matchDependencyPattern(watchedPattern, name);
    if (matchResult?.isMatch) {
      const { parentCode, arrayIndex } = matchResult;
      dependents.forEach(dependent => {
        let d = dependent;

        if (arrayIndex !== null) {
          const { parentCode: dParentCode } = splitArrayPath(dependent);
          if (dParentCode === parentCode && d.includes('{i}')) {
            d = d.replace(/\{i\}/g, arrayIndex);
          }
        }

        out.push(d);
      });
    }
  });

  return out;
};

/**
 * Collects dependents from a flat values snapshot.
 * Use for initial collection of dependents based on all existing values.
 */
export const getDependentsFromValues = (
  valuesSnapshot,
  dependencyMap,
  { includeIndexes = false, ignoreEmpty = true } = {},
) => {
  const out = new Set();
  const dotPaths = convertObjectToDotPaths(valuesSnapshot || {});
  for (const [path, value] of Object.entries(dotPaths)) {
    if (ignoreEmpty && !hasNonEmpty(value)) continue;
    const deps = includeIndexes
      ? getDependentsWithIndexes(path, dependencyMap)
      : getDependents(path, dependencyMap);
    deps.forEach(d => out.add(d));
  }
  return [...out];
};

// Dynamic: update sections only (no form value writes)
export const runDynamicDependents = (
  dependents,
  sections,
  fieldPathIndex,
  dependencyValue,
  valuesSnapshot,
  templateSources = {},
) => {
  if (!Array.isArray(dependents) || dependents.length === 0) return null;
  let latest = sections || [];
  let anyChange = false;

  dependents.forEach(depPath => {
    const { updatedSections, changed } = processDynamicContent(
      latest,
      fieldPathIndex,
      depPath,
      dependencyValue,
      valuesSnapshot,
      templateSources,
    );
    if (changed && updatedSections) {
      latest = updatedSections;
      anyChange = true;
    }
  });

  if (anyChange) {
    return latest;
  }
  return null;
};

export const runDynamicDependentsForHiddenUpdates = (
  hiddenUpdates,
  sections,
  fieldPathIndex,
  valuesSnapshot,
  dynamicDependencyCodes,
  templateSources = {},
) => {
  if (!hiddenUpdates || !sections) {
    return null;
  }

  let latest = sections;
  let anyChange = false;

  for (const [key, value] of Object.entries(hiddenUpdates)) {
    if (value === undefined) {
      continue;
    }
    const deps = getDependents(key, dynamicDependencyCodes);
    if (!deps.length) {
      continue;
    }
    const updated = runDynamicDependents(
      deps,
      latest,
      fieldPathIndex,
      value,
      valuesSnapshot,
      templateSources,
    );
    if (updated) {
      latest = updated;
      anyChange = true;
    }
  }

  return anyChange ? latest : null;
};

export const runHiddenDependentsChained = (
  initialDependents,
  values,
  hiddenFields,
  hiddenDependencyCodes,
  maxPasses = 2,
) => {
  let valuesForHidden = values;
  let accumulatedUpdates = {};
  let dependents = initialDependents || [];

  for (let pass = 0; pass < maxPasses; pass++) {
    if (!dependents.length) {
      break;
    }
    const passUpdates = runHiddenDependents(dependents, valuesForHidden, hiddenFields);
    if (!passUpdates) {
      break;
    }
    accumulatedUpdates = { ...accumulatedUpdates, ...passUpdates };
    valuesForHidden = { ...valuesForHidden, ...passUpdates };
    const nextDependents = Object.keys(passUpdates).flatMap(
      key => hiddenDependencyCodes[key] || [],
    );
    dependents = [...new Set(nextDependents)];
  }

  return Object.keys(accumulatedUpdates).length > 0 ? accumulatedUpdates : null;
};

// Hidden: compute values only (no section mutation). Returns { code: value, ... }
export const runHiddenDependents = (dependents, values, hiddenFields) => {
  const updates = {};

  for (const fieldPath of dependents) {
    const field = hiddenFields[fieldPath];
    if (!field?.runtimeConfig) continue;

    const processorName = field.runtimeConfig.processor;
    const processorParams = field.runtimeConfig.processorParams || {};

    const fn = SOURCES[processorName];
    if (typeof fn !== 'function') continue;

    const result = fn(values, processorParams);
    if (result !== undefined) {
      updates[fieldPath] = result;
    }
  }

  return Object.keys(updates).length > 0 ? updates : null;
};

export const runDependentValidators = (
  dependents, // string[]
  sectionPrefixes,
  wrapperValidators,
  allValues,
  trigger,
  setError,
  clearErrors,
) => {
  dependents.forEach(dep => {
    // 1) RHF field-level validations: trigger only those in this section
    const matches = Object.keys(allValues)
      .filter(k => {
        // Always include exact field match
        if (k === dep) return true;

        // For descendant fields, only include if it's not an array field
        // Array fields use wrapper validation, not individual subfield validation
        if (k.startsWith(dep + '.')) {
          // Check if this is an array field by looking at the wrapper validators
          const wrapperKey = dep.split('.')[0];
          const isArrayField = wrapperValidators?.[wrapperKey];

          // If it's an array field, don't include subfields (only the wrapper)
          if (isArrayField) return false;

          // For non-array fields (like address), include all subfields
          return true;
        }

        return false;
      })
      .filter(k => belongsToCurrentPage(k, sectionPrefixes));

    // Only trigger field-level validations for non-array fields
    // Array fields use wrapper validation, and triggering them might clear wrapper errors
    // IMPORTANT: Don't trigger wrapper fields - they use manual setError, not RHF validation
    matches.forEach(k => {
      // Check if this is an array field wrapper - if so, skip trigger to preserve manual errors
      const isArrayWrapper = wrapperValidators?.[k];
      if (!isArrayWrapper) {
        trigger(k);
      }
    });

    if (dep.includes('.') && belongsToCurrentPage(dep, sectionPrefixes)) {
      trigger(dep);
    }

    // 2) Wrapper-level manual validators: only if wrapper is in this section
    const wrapperKey = dep.split('.')[0]; // Get the parent part (e.g., "academic_qualifications" from "academic_qualifications.0.name")

    if (wrapperValidators?.[wrapperKey]) {
      const belongsToPage = belongsToCurrentPage(wrapperKey, sectionPrefixes);

      if (belongsToPage) {
        const wrapperValidator = wrapperValidators[wrapperKey];
        const wrapperValidatorName = wrapperValidator?.validatorName;
        const validator = wrapperValidatorName && functionValidators?.[wrapperValidatorName];

        if (validator?.validate) {
          const result = validator.validate(wrapperValidator?.validationParams, allValues);
          if (result !== true) {
            const errorMessage =
              typeof result === 'string'
                ? result
                : result?.error || result?.message || 'Validation failed';
            // Set error when validation fails
            setError(wrapperKey, { type: 'manual', message: errorMessage }, { shouldFocus: false });
          } else {
            // Clear error when validation passes
            clearErrors(wrapperKey);
          }
        }
      }
    }
  });
};
